// Obuna (Q-064): oylik summa, to'lov qabul qilish, to'lovlar tarixi, avtomatik to'xtatish.
// To'lov kiritilishi bilan muddat o'zi uzayadi (kunlar yo'qolmaydi); xato to'lov sabab bilan bekor qilinadi.
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { addonLabel, parseAmount } from '@onepos/shared';
import {
  Alert,
  Badge,
  Button,
  Card,
  DataList,
  Icon,
  Modal,
  SelectField,
  Spinner,
  Switch,
  TextAreaField,
  TextField,
  useAction,
  useAsync,
} from '@onepos/ui';
import { api } from '../../lib/api';
import { useMe } from '../../lib/auth';
import { addMonths, amountDiffers, payStart } from '../../lib/billing';
import { PAY_METHODS, STATUS, fmtDate, fmtDaysLeft, fmtMoney, monthsText } from '../../lib/format';
import type { BillingPayment, BillingTenant, PayMethod, TenantDetail } from '../../lib/types';

const PERIODS = [1, 3, 6, 12] as const;

export function BillingTab({ data, reload }: { data: TenantDetail; reload: () => void }) {
  const me = useMe();
  const canEdit = me.role === 'owner' || me.role === 'billing';
  const b = useAsync(() => api.rpc<BillingTenant>('platform_billing_tenant', { p_tenant: data.tenant.id }), [data.tenant.id]);
  const [pay, setPay] = useState(false);
  const [auto, setAuto] = useState(false);
  const [voiding, setVoiding] = useState<BillingPayment | null>(null);
  const [terms, setTerms] = useState<'lock' | 'unlock' | 'privilege' | null>(null);

  if (b.loading && !b.data) return <Spinner />;
  if (b.error) return <Alert tone="danger">{b.error.message}</Alert>;
  const x = b.data;
  if (!x) return null;
  const done = () => {
    b.reload();
    reload();
  };
  const lastLive = x.payments.find((p) => !p.voided_at);
  const deleted = x.status === 'deleted';

  return (
    <>
      <div className="cn-two">
        <Card
          title="Obuna holati"
          actions={
            canEdit &&
            !deleted && (
              <Button size="sm" variant="primary" onClick={() => setPay(true)}>
                <Icon name="plus" /> To‘lov qabul qilindi
              </Button>
            )
          }
        >
          <DataList
            items={[
              { label: 'Holat', value: <Badge tone={STATUS[x.status].tone}>{STATUS[x.status].label}</Badge> },
              {
                label: x.status === 'trial' ? 'Sinov tugashi' : 'To‘langan muddat',
                value: x.ends_at ? `${fmtDate(x.ends_at)} (${fmtDaysLeft(x.ends_at)})` : '—',
              },
              {
                label: 'Muddat tugasa',
                value: x.auto_suspend
                  ? `${x.grace_days} kun ogohlantirish, keyin to‘xtatiladi${x.suspend_at ? ` (${fmtDate(x.suspend_at)})` : ''}`
                  : 'To‘xtatilmaydi — faqat ogohlantirish',
              },
              { label: 'To‘lov izohi uchun', value: <span className="op-num">{x.login ?? '—'}</span> },
            ]}
          />
          {canEdit && !deleted && (
            <div className="cn-row" style={{ marginTop: 12 }}>
              <Button size="sm" onClick={() => setAuto(true)}>
                <Icon name="clock" /> Avtomatik to‘xtatish
              </Button>
            </div>
          )}
        </Card>

        <QuoteCard x={x} />
      </div>

      <TermsCard
        x={x}
        canEdit={canEdit && !deleted}
        isOwner={me.role === 'owner'}
        onLock={() => setTerms('lock')}
        onUnlock={() => setTerms('unlock')}
        onPrivilege={() => setTerms('privilege')}
      />

      <Card title="To‘lovlar tarixi" actions={<Badge tone="neutral">{x.payments.filter((p) => !p.voided_at).length}</Badge>}>
        {x.payments.length === 0 ? (
          <p className="cn-muted">Hali to‘lov kiritilmagan.</p>
        ) : (
          <div className="cn-table-wrap">
            <table className="cn-table">
              <thead>
                <tr>
                  <th>Sana</th>
                  <th>Davr</th>
                  <th className="cn-num">Summa</th>
                  <th>Usul</th>
                  <th>Kim</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {x.payments.map((p) => (
                  <tr key={p.id} className={p.voided_at ? 'cn-row-void' : undefined}>
                    <td data-label="Sana">
                      {fmtDate(p.created_at)}
                      {p.voided_at && (
                        <span className="cn-table__sub">
                          Bekor qilingan {fmtDate(p.voided_at)}
                          {p.voided_by ? ` · ${p.voided_by}` : ''} · {p.void_reason}
                        </span>
                      )}
                    </td>
                    <td data-label="Davr">
                      {monthsText(p.months)}
                      <span className="cn-table__sub">
                        {fmtDate(p.starts_at)} – {fmtDate(p.ends_at)}
                      </span>
                    </td>
                    <td data-label="Summa" className="cn-num op-num">
                      {p.voided_at ? <s>{fmtMoney(p.amount)}</s> : fmtMoney(p.amount)}
                      {p.quoted != null && p.quoted !== p.amount && <span className="cn-table__sub">hisob: {fmtMoney(p.quoted)}</span>}
                    </td>
                    <td data-label="Usul">
                      {PAY_METHODS[p.method] ?? p.method}
                      {(p.reference || p.note) && <span className="cn-table__sub">{[p.reference, p.note].filter(Boolean).join(' · ')}</span>}
                    </td>
                    <td data-label="Kim">{p.recorded_by ?? '—'}</td>
                    <td data-label="">
                      {canEdit && lastLive?.id === p.id && (
                        <Button size="sm" variant="ghost" onClick={() => setVoiding(p)}>
                          Bekor qilish
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {pay && <PayModal x={x} tenantId={data.tenant.id} onClose={() => setPay(false)} onDone={done} />}
      {auto && <AutoModal x={x} tenantId={data.tenant.id} onClose={() => setAuto(false)} onDone={done} />}
      {voiding && <VoidModal p={voiding} onClose={() => setVoiding(null)} onDone={done} />}
      {terms && <TermsModal what={terms} x={x} tenantId={data.tenant.id} onClose={() => setTerms(null)} onDone={done} />}
    </>
  );
}

function QuoteCard({ x }: { x: BillingTenant }) {
  const q = x.quote;
  if (q.plan_price === null) {
    return (
      <Card title="Oylik summa">
        <Alert tone="info" title={`“${q.plan_label}” tarifining narxi belgilanmagan`}>
          Narxni <Link to="/tariflar">Tariflar</Link> sahifasida kiriting — shunda summa o‘zi hisoblanadi.
        </Alert>
      </Card>
    );
  }
  return (
    <Card title="Oylik summa">
      <ul className="cn-list cn-list--kv">
        <li>
          <span className="cn-list__main">
            {q.plan_label} tarifi
            {q.list_price != null && q.list_price !== q.plan_price && (
              <span className="cn-list__sub">kafolat narxi · amaldagi narx {fmtMoney(q.list_price)}</span>
            )}
          </span>
          <span className="op-num">{fmtMoney(q.plan_price)}</span>
        </li>
        {q.addons.map((a) => (
          <li key={a.key}>
            <span className="cn-list__main">
              + {addonLabel(a)}
              <span className="cn-list__sub">
                {a.key.startsWith('module.') ? 'tarifdan tashqari' : 'tarifdan ortiq'} ·{' '}
                {a.price > 0 ? `${fmtMoney(a.price)} dan` : 'narx belgilanmagan — bepul'}
              </span>
            </span>
            <span className="op-num">{fmtMoney(a.total)}</span>
          </li>
        ))}
        <li>
          <strong>Oyiga</strong>
          <strong className="op-num">{fmtMoney(q.monthly ?? 0)}</strong>
        </li>
      </ul>
      <div className="cn-tags" style={{ marginTop: 10 }}>
        {q.periods.map((p) => (
          <span key={p.months} className="cn-tag">
            {monthsText(p.months)}: {fmtMoney(p.amount ?? 0)}
            {p.discount > 0 ? ` (−${p.discount}%)` : ''}
          </span>
        ))}
      </div>
    </Card>
  );
}

/** Narx sharti (Q-079): imtiyoz va narx kafolati */
function TermsCard({
  x,
  canEdit,
  isOwner,
  onLock,
  onUnlock,
  onPrivilege,
}: {
  x: BillingTenant;
  canEdit: boolean;
  isOwner: boolean;
  onLock: () => void;
  onUnlock: () => void;
  onPrivilege: () => void;
}) {
  const lock = x.price_lock ?? null;
  const live = x.quote.lock ?? null;
  return (
    <Card
      title="Narx sharti"
      actions={x.privileged ? <Badge tone="info">Imtiyozli</Badge> : live ? <Badge tone="success">Kafolat</Badge> : undefined}
    >
      <DataList
        items={[
          {
            label: 'Imtiyoz',
            value: x.privileged ? 'Imtiyozli — qurilma va xodim limiti yo‘q, qo‘shimchalar hisoblanmaydi' : 'Yo‘q — tarif bo‘yicha',
          },
          {
            label: 'Narx kafolati',
            value: live
              ? `${live.until ? `${fmtDate(live.until)} gacha` : 'Muddatsiz'}${lock?.reason ? ` · ${lock.reason}` : ''}`
              : lock
                ? `Amal qilmaydi (${lock.plan !== x.quote.plan_id ? 'tarif o‘zgargan' : 'muddati o‘tgan'})`
                : 'Yo‘q — amaldagi narx',
          },
        ]}
      />
      {canEdit && (
        <div className="cn-row" style={{ marginTop: 12 }}>
          <Button size="sm" onClick={onLock}>
            <Icon name="lock" /> Narxni muzlatish
          </Button>
          {lock && (
            <Button size="sm" variant="ghost" onClick={onUnlock}>
              <Icon name="unlock" /> Kafolatni olib tashlash
            </Button>
          )}
          {isOwner && (
            <Button size="sm" variant="ghost" onClick={onPrivilege}>
              <Icon name="shield" /> {x.privileged ? 'Imtiyozni olib tashlash' : 'Imtiyozli qilish'}
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}

const LOCK_SPANS = [
  { key: '12', label: '1 yil' },
  { key: '24', label: '2 yil' },
  { key: 'none', label: 'Muddatsiz' },
] as const;

function TermsModal({
  what,
  x,
  tenantId,
  onClose,
  onDone,
}: {
  what: 'lock' | 'unlock' | 'privilege';
  x: BillingTenant;
  tenantId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [span, setSpan] = useState<(typeof LOCK_SPANS)[number]['key']>('24');
  const [reason, setReason] = useState('');
  const until = useMemo(() => {
    if (span === 'none') return null;
    const d = new Date();
    d.setMonth(d.getMonth() + Number(span));
    return d.toISOString().slice(0, 10);
  }, [span]);
  const act = useAction(async () => {
    if (what === 'lock') await api.rpc('platform_price_lock_set', { p_tenant: tenantId, p_until: until, p_reason: reason.trim() });
    else if (what === 'unlock') await api.rpc('platform_price_lock_clear', { p_tenant: tenantId, p_reason: reason.trim() });
    else await api.rpc('platform_privilege_set', { p_tenant: tenantId, p_on: !x.privileged, p_reason: reason.trim() });
    onDone();
    onClose();
  });
  const title =
    what === 'lock'
      ? 'Narxni muzlatish'
      : what === 'unlock'
        ? 'Narx kafolatini olib tashlash'
        : x.privileged
          ? 'Imtiyozni olib tashlash'
          : 'Imtiyozli restoran';
  return (
    <Modal
      open
      onClose={onClose}
      busy={act.busy}
      title={title}
      footer={
        <>
          <Button onClick={onClose} disabled={act.busy}>
            Bekor qilish
          </Button>
          <Button variant="primary" disabled={act.busy || reason.trim().length < 3} onClick={() => void act.run()}>
            Saqlash
          </Button>
        </>
      }
    >
      {act.error && <Alert tone="danger">{act.error}</Alert>}
      {what === 'lock' && (
        <>
          <p className="cn-muted" style={{ marginTop: 0 }}>
            Hozirgi narx ({fmtMoney(x.quote.plan_price ?? 0)} + qo‘shimchalar) saqlanadi. Tarif narxi oshsa ham restoran shu narxda
            to‘laydi; tushsa — arzoni qo‘llanadi. Boshqa tarifga o‘tsa, kafolat amal qilmaydi.
          </p>
          <div className="cn-tags" role="group" aria-label="Muddat">
            {LOCK_SPANS.map((s) => (
              <button
                key={s.key}
                type="button"
                className={`cn-chip${span === s.key ? ' cn-chip--on' : ''}`}
                aria-pressed={span === s.key}
                onClick={() => setSpan(s.key)}
              >
                {s.label}
              </button>
            ))}
          </div>
          <p className="cn-muted">{until ? `${fmtDate(until)} gacha` : 'Muddatsiz — qo‘lda olib tashlanguncha'}</p>
        </>
      )}
      {what === 'unlock' && (
        <p className="cn-muted" style={{ marginTop: 0 }}>
          Restoran keyingi to‘lovdan tarifning amaldagi narxida to‘laydi.
        </p>
      )}
      {what === 'privilege' && (
        <p className="cn-muted" style={{ marginTop: 0 }}>
          {x.privileged
            ? 'Tarif limitlari va qo‘shimchalar hisobi qaytadi. Ishlab turgan qurilmalar uzilmaydi, xodimlar o‘chmaydi.'
            : 'Qurilma va xodim limiti olib tashlanadi, qo‘shimchalar hisoblanmaydi. Tarif narxi (va sinov muddati) o‘zgarmaydi.'}
        </p>
      )}
      <TextAreaField
        label="Sabab (jurnalga yoziladi)"
        rows={2}
        value={reason}
        maxLength={300}
        onChange={(e) => setReason(e.target.value)}
        placeholder={what === 'lock' ? 'Masalan: birinchi 100 mijoz — 2 yil kafolat' : 'Masalan: ega qarori'}
      />
    </Modal>
  );
}

function PayModal({ x, tenantId, onClose, onDone }: { x: BillingTenant; tenantId: string; onClose: () => void; onDone: () => void }) {
  const [months, setMonths] = useState<number>(1);
  const quoted = x.quote.periods.find((p) => p.months === months)?.amount ?? null;
  const [amountText, setAmountText] = useState<string>(quoted != null ? String(quoted) : '');
  const [touched, setTouched] = useState(false);
  const [method, setMethod] = useState<PayMethod>('transfer');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const request = useMemo(() => crypto.randomUUID(), []);
  const amount = amountText.trim() === '' ? null : parseAmount(amountText);
  const differs = amountDiffers(amount, quoted);
  const start = payStart(x);
  const end = addMonths(start, months);
  const act = useAction(async () => {
    await api.rpc('platform_billing_pay', {
      p_tenant: tenantId,
      p_months: months,
      p_amount: amount,
      p_method: method,
      p_reference: reference || null,
      p_note: note || null,
      p_request: request,
    });
    onDone();
    onClose();
  });
  const pick = (n: number) => {
    setMonths(n);
    if (!touched) {
      const q = x.quote.periods.find((p) => p.months === n)?.amount;
      setAmountText(q != null ? String(q) : '');
    }
  };
  const invalid = amount === null || !Number.isInteger(amount) || amount < 0 || (differs && note.trim().length < 3);

  return (
    <Modal
      open
      onClose={onClose}
      busy={act.busy}
      title="To‘lov qabul qilindi"
      footer={
        <>
          <Button onClick={onClose} disabled={act.busy}>
            Bekor qilish
          </Button>
          <Button variant="primary" disabled={act.busy || invalid} onClick={() => void act.run()}>
            Saqlash
          </Button>
        </>
      }
    >
      {act.error && <Alert tone="danger">{act.error}</Alert>}
      <div className="cn-field-label">Davr</div>
      <div className="cn-row" role="group" aria-label="Davr">
        {PERIODS.map((n) => {
          const p = x.quote.periods.find((y) => y.months === n);
          return (
            <button key={n} type="button" className={`cn-chip${months === n ? ' cn-chip--on' : ''}`} aria-pressed={months === n} onClick={() => pick(n)}>
              {monthsText(n)}
              {p && p.discount > 0 ? ` · −${p.discount}%` : ''}
            </button>
          );
        })}
      </div>
      <TextField
        label="Summa, so‘m"
        inputMode="numeric"
        value={amountText}
        onChange={(e) => {
          setTouched(true);
          setAmountText(e.target.value.replace(/[^\d\s]/g, ''));
        }}
        hint={quoted != null ? `Hisoblangan: ${fmtMoney(quoted)}` : 'Tarif narxi belgilanmagan — summani o‘zingiz kiriting'}
        error={amount === null && amountText.trim() !== '' ? 'Summa noto‘g‘ri' : undefined}
      />
      <SelectField
        label="Usul"
        value={method}
        onChange={(e) => setMethod(e.target.value as PayMethod)}
        options={Object.entries(PAY_METHODS).map(([value, label]) => ({ value, label }))}
      />
      <TextField label="Hujjat raqami (ixtiyoriy)" value={reference} maxLength={120} onChange={(e) => setReference(e.target.value)} placeholder="Masalan: to‘lov topshiriqnomasi № 15" />
      <TextAreaField
        label={differs ? 'Sabab (summa hisobdan farq qiladi — majburiy)' : 'Izoh (ixtiyoriy)'}
        rows={2}
        value={note}
        maxLength={500}
        onChange={(e) => setNote(e.target.value)}
      />
      <Alert tone="info" title="Yangi muddat">
        {fmtDate(start)} – {fmtDate(end)}. {x.status === 'suspended' ? 'Restoran darhol qayta ochiladi. ' : ''}
        {x.status === 'trial' && x.trial_ends_at && Date.parse(x.trial_ends_at) > Date.now() ? 'Sinovning qolgan kunlari saqlanadi. ' : ''}
        {x.status === 'grace' ? 'Muddat uzluksiz davom etadi (tugagan kundan). ' : ''}
      </Alert>
    </Modal>
  );
}

function AutoModal({ x, tenantId, onClose, onDone }: { x: BillingTenant; tenantId: string; onClose: () => void; onDone: () => void }) {
  const [on, setOn] = useState(x.auto_suspend);
  const [grace, setGrace] = useState(String(x.grace_days));
  const graceOk = /^\d+$/.test(grace) && Number(grace) <= 60;
  const act = useAction(async () => {
    await api.rpc('platform_billing_set', { p_tenant: tenantId, p_auto_suspend: on, p_grace_days: Number(grace) });
    onDone();
    onClose();
  });
  return (
    <Modal
      open
      onClose={onClose}
      busy={act.busy}
      title="Muddat tugaganda"
      footer={
        <>
          <Button onClick={onClose} disabled={act.busy}>
            Bekor qilish
          </Button>
          <Button variant="primary" disabled={act.busy || !graceOk} onClick={() => void act.run()}>
            Saqlash
          </Button>
        </>
      }
    >
      {act.error && <Alert tone="danger">{act.error}</Alert>}
      <Switch
        checked={on}
        onChange={setOn}
        label="Avtomatik to‘xtatish"
        description="Muhlat tugagach restoran faqat ko‘rish rejimiga o‘tadi (tarix va hisobotlar ochiq). Savdo o‘rtasida emas — restoran bo‘sh paytda."
      />
      <TextField
        label="Muhlat, kun (0–60)"
        inputMode="numeric"
        value={grace}
        onChange={(e) => setGrace(e.target.value.replace(/\D/g, '').slice(0, 2))}
        hint="Muddat tugagandan keyin shuncha kun restoran ogohlantirish bilan ishlaydi"
        error={graceOk ? undefined : '0 dan 60 gacha'}
      />
    </Modal>
  );
}

function VoidModal({ p, onClose, onDone }: { p: BillingPayment; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const act = useAction(async () => {
    await api.rpc('platform_billing_void', { p_payment: p.id, p_reason: reason });
    onDone();
    onClose();
  });
  return (
    <Modal
      open
      onClose={onClose}
      busy={act.busy}
      title="To‘lovni bekor qilish"
      footer={
        <>
          <Button onClick={onClose} disabled={act.busy}>
            Yopish
          </Button>
          <Button variant="danger" disabled={act.busy || reason.trim().length < 3} onClick={() => void act.run()}>
            Bekor qilish
          </Button>
        </>
      }
    >
      {act.error && <Alert tone="danger">{act.error}</Alert>}
      <p style={{ marginTop: 0 }}>
        {fmtMoney(p.amount)} · {monthsText(p.months)} ({fmtDate(p.starts_at)} – {fmtDate(p.ends_at)}). To‘lov o‘chirilmaydi — tarixda
        “bekor qilingan” bo‘lib qoladi. Muddat va holat to‘lovdan oldingi holatga qaytadi.
      </p>
      <TextAreaField label="Sabab (majburiy)" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
    </Modal>
  );
}
